/* Build in libraries */

/* Third party libraries */

import ActiveProjectsWidget from '@/components/Rover/Widgets/ActiveProjectsWidget';
/* Components */
import ApprovedProjectsWidget from '@/components/Rover/Widgets/ApprovedProjectsWidget';
import TotalAcquisitionCostWidget from '@/components/Rover/Widgets/TotalAcquisitionCostWidget';
import TotalDevelopmentCostWidget from '@/components/Rover/Widgets/TotalDevelopmentCostWidget';
import TotalPreDevelopmentCostWidget from '@/components/Rover/Widgets/TotalPreDevelopmentCostWidget';
import TotalProposedSqFtWidget from '@/components/Rover/Widgets/TotalProposedSqFtWidget';
import TotalProposedUnitsWidget from '@/components/Rover/Widgets/TotalProposedUnitsWidget';
import Card from '@/components/UI/Card/index.tsx';
/* Services */
import PieChart from '@/components/UI/Charts/PieChart';
import { isObjectEmpty, transformProjectTypeChartDataSets } from '@/utils';

interface IApplicationDashboardComponentProps {
  data: any;
}

export const BTPPDashboard = ({ data }: IApplicationDashboardComponentProps) => {
  const applicationsList = data?.data;

  const widgetsData: {
    totalDevelopmentCost: number;
    totalAcquisitionCost: number;
    totalPreDevelopmentCost: number;
    totalProposedUnits: number;
    totalActiveProjects: number;
    totalApprovedProjects: number;
    totalProposedSqft: number;
    statusSummary: { [key: string]: number };
    acquisitionTypes: { [key: string]: number };
    dealTypes: { [key: string]: number };
    constructionTypes: { [key: string]: number };
  } = {
    totalDevelopmentCost: 0,
    totalAcquisitionCost: 0,
    totalPreDevelopmentCost: 0,
    totalProposedUnits: 0,
    totalActiveProjects: 0,
    totalApprovedProjects: 0,
    totalProposedSqft: 0,
    statusSummary: {},
    acquisitionTypes: {},
    dealTypes: {},
    constructionTypes: {},
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;
    //Total development cost widget
    widgetsData.totalDevelopmentCost += applicationData?.projectDetails?.developmentCostEstimate;

    //Total acquisition cost widget
    widgetsData.totalAcquisitionCost += applicationData?.projectDetails?.acquisitionCostEstimate;

    //Total pre-development cost widget
    widgetsData.totalPreDevelopmentCost += applicationData?.projectDetails?.preDevelopmentCostEstimate;

    //Total proposed units cost widget
    widgetsData.totalProposedUnits += applicationData?.projectDetails?.proposedUnits;

    //Total active projects widget
    if (a.status !== 'DENIED') {
      widgetsData.totalActiveProjects += 1;
    }

    //Total approved projects widget
    if (a.status === 'APPROVED') {
      widgetsData.totalApprovedProjects += 1;
    }

    //Status Summary
    widgetsData.statusSummary[a.status] = (widgetsData.statusSummary[a.status] ?? 0) + 1;

    //Acquisition Type Summary
    const { acquisitionType } = applicationData?.projectDetails || {};
    widgetsData.acquisitionTypes[acquisitionType] = (widgetsData.acquisitionTypes[acquisitionType] ?? 0) + 1;

    //Deal Type Summary
    const { dealType } = applicationData?.projectDetails || {};
    widgetsData.dealTypes[dealType] = (widgetsData.dealTypes[dealType] ?? 0) + 1;

    //Construction Type Summary
    const { constructionType } = applicationData?.projectDetails || {};
    widgetsData.constructionTypes[constructionType] = (widgetsData.constructionTypes[constructionType] ?? 0) + 1;
  });

  const transformDataForPieChart = (data: { [key: string]: number }) => {
    const transformedData = Object.keys(data).map((d) => ({
      type: d,
      count: data[d],
    }));

    return transformProjectTypeChartDataSets(transformedData);
  };

  return (
    <div className='max-w-screen-3xl mx-auto'>
      <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
        <TotalDevelopmentCostWidget count={widgetsData?.totalDevelopmentCost ?? 0} />
        <TotalAcquisitionCostWidget count={widgetsData?.totalAcquisitionCost ?? 0} />
        <TotalPreDevelopmentCostWidget count={widgetsData?.totalPreDevelopmentCost ?? 0} />
        <TotalProposedUnitsWidget count={widgetsData?.totalProposedUnits ?? 0} />
      </section>
      <section className='my-4 grid md:grid-cols-3 xl:grid-cols-3 gap-4'>
        <ActiveProjectsWidget count={widgetsData?.totalActiveProjects ?? 0} />
        <ApprovedProjectsWidget count={widgetsData?.totalApprovedProjects ?? 0} />
        <TotalProposedSqFtWidget count={widgetsData?.totalProposedSqft ?? 0} />
      </section>
      <div className='my-4'>
        <div className='bg-white rounded-md flex flex-row px-4 py-6 relative'>
          <div className='flex-grow flex flex-col justify-between font-bold'>
            <div className='flex gap-6 items-center'>
              <h4>Summary Status:</h4>
              <div className='flex items-center gap-2'>
                <div className='w-3 h-3 bg-neutralColor-normal rounded-full'></div>
                <h5 className='font-medium'>Submitted: {widgetsData.statusSummary?.SUBMITTED ?? 0}</h5>
              </div>
              <div className='flex items-center gap-2'>
                <div className='w-3 h-3 bg-secondaryColor-normal rounded-full'></div>
                <h5 className='font-medium'>Under Review: {widgetsData.statusSummary?.UNDER_REVIEW ?? 0}</h5>
              </div>
              <div className='flex items-center gap-2'>
                <div className='w-3 h-3 bg-blue-500 rounded-full'></div>
                <h5 className='font-medium'>Document Requested: {widgetsData.statusSummary?.DOCUMENT_REQUESTED ?? 0}</h5>
              </div>
              <div className='flex items-center gap-2'>
                <div className='w-3 h-3 bg-primaryColor-normal rounded-full'></div>
                <h5 className='font-medium'>Approved: {widgetsData.statusSummary?.APPROVED ?? 0}</h5>
              </div>
              <div className='flex items-center gap-2'>
                <div className='w-3 h-3 bg-tertiaryColor-normal rounded-full'></div>
                <h5 className='font-medium'>Denied: {widgetsData.statusSummary?.DENIED ?? 0}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* TO Do */}
      {/* <div className='my-4'>
          <Card title='Project Location'>
            {false && (
              <div className='w-full flex justify-center items-center h-screen/2'>
                <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                  <MapPinIcon className='h-8 w-8' />
                  <h1>No Projects</h1>
                </div>
                <p>There are no assigned projects to display.</p>
              </div>
            )}
            {true && (
              <div className='w-full grid grid-cols-6 h-screen/2'>
                <div className='col-span-6 w-full border-l p-5'>
                  <MapGoogle projectList={dashboardData?.project_location} />
                </div>
              </div>
            )}
          </Card>
        </div> */}
      <section className='grid md:grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-8 my-4'>
        {!isObjectEmpty(widgetsData?.acquisitionTypes) && (
          <Card title='Acquisition Types'>
            <div className='relative p-5 h-80'>
              <div className='absolute px-5 text-center'>
                <h3 className='text-gray-500 font-bold'>Total Projects</h3>
                <h1 className='m-0'>{}</h1>
              </div>
              <div className='md:w-7/8 xl:w-full h-full'>
                <PieChart
                  labels={Object.keys(widgetsData.acquisitionTypes)}
                  datasets={transformDataForPieChart(widgetsData.acquisitionTypes)}
                  chartTitle='Total Project Types'
                />
              </div>
            </div>
          </Card>
        )}
        {!isObjectEmpty(widgetsData?.dealTypes) && (
          <Card title='Deal Types'>
            <div className='relative p-5 h-80'>
              <div className='absolute px-5 text-center'>
                <h3 className='text-gray-500 font-bold'>Total Projects</h3>
                <h1 className='m-0'>{}</h1>
              </div>
              <div className='md:w-7/8 xl:w-full h-full'>
                <PieChart
                  labels={Object.keys(widgetsData.dealTypes)}
                  datasets={transformDataForPieChart(widgetsData.dealTypes)}
                  chartTitle='Total Deal Types'
                />
              </div>
            </div>
          </Card>
        )}
        {!isObjectEmpty(widgetsData?.constructionTypes) && (
          <Card title='Construction Types'>
            <div className='relative p-5 h-80'>
              <div className='absolute px-5 text-center'>
                <h3 className='text-gray-500 font-bold'>Total Projects</h3>
                <h1 className='m-0'>{}</h1>
              </div>
              <div className='md:w-7/8 xl:w-full h-full'>
                <PieChart
                  labels={Object.keys(widgetsData.constructionTypes)}
                  datasets={transformDataForPieChart(widgetsData.constructionTypes)}
                  chartTitle='Total Construction Types'
                />
              </div>
            </div>
          </Card>
        )}
      </section>
    </div>
  );
};
